import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Info, Cpu, Settings, LayoutGrid, Briefcase, Mail, ChevronDown } from 'lucide-react';
import Logo from '../assets/img/OnesoftLogo.png';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  dropdown?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    label: 'About Us',
    href: '/about',
    icon: <Info size={16} />,
    dropdown: [
      { label: 'About Us', href: '/about' },
      { label: 'Infra Structure', href: '/about/infrastructure' },
      { label: 'Mission & Values', href: '/about/mission' },
      { label: 'Out Source', href: '/about/outsource' },
      { label: 'Why OneSoft', href: '/about/whyonesoft' },
    ],
  },
  {
    label: 'Methodology',
    href: '/methodology',
    icon: <Cpu size={16} />,
    dropdown: [
      { label: 'Methodology', href: '/methodology' },
      { label: 'Our Technologies', href: '/methodology/technology' },
      { label: 'Quality Control', href: '/methodology/quality' },
      { label: 'Project Flow', href: '/methodology/project-flow' },
    ],
  },
  {
    label: 'Services',
    href: '/services',
    icon: <Settings size={16} />,
    dropdown: [
      { label: 'All Services', href: '/services' },
      { label: 'Application Support', href: '/services/application-support' },
      { label: 'Business Analysis', href: '/services/business-analysis' },
      { label: 'Consulting', href: '/services/consulting' },
      { label: 'Software Design', href: '/services/software-design' },
      { label: 'Ecommerce', href: '/services/ecommerce' },
      { label: 'ERP', href: '/services/erp' },
      { label: 'Offshore Team', href: '/services/offshore' },
      { label: 'Payment Gateway', href: '/services/payment-gateway' },
      { label: 'QA & Testing', href: '/services/quality' },
      { label: 'System Integration', href: '/services/system-integration' },
      { label: 'Web Technologies', href: '/services/web-technologies' },
      { label: 'Wireless & Mobile', href: '/services/wireless-mobile' },
    ],
  },
  {
    label: 'Verticals',
    href: '/verticals',
    icon: <LayoutGrid size={16} />,
    dropdown: [
      { label: 'Verticals', href: '/verticals' },
      { label: 'Education', href: '/verticals/education' },
      { label: 'Healthcare', href: '/verticals/healthcare' },
      { label: 'Payroll Solutions', href: '/verticals/payroll' },
      { label: 'Manufacturing', href: '/verticals/manufacturing' },
      { label: 'Retail', href: '/verticals/retail' },
      { label: 'Telecommunications', href: '/verticals/telecommunications' },
    ],
  },
  {
    label: 'Careers',
    href: '/careers',
    icon: <Briefcase size={16} />,
    dropdown: [
      { label: 'Careers', href: '/careers' },
      { label: 'Culture', href: '/careers/culture' },
      { label: 'Current Openings', href: '/careers/openings' },
      { label: 'Why Us', href: '/careers/why-us' },
      { label: 'Looking For', href: '/careers/looking-for' },
    ],
  },
  {
    label: 'Contact Us',
    href: '/contact',
    icon: <Mail size={16} />,
    dropdown: [
      { label: 'Contact Us', href: '/contact' },
      { label: 'Aliances & Partner', href: '/contact/aliances' },
    ],
  },
  {
    label: 'Projects',
    href: '/projects',
    icon: <LayoutGrid size={16} />,
  },
];

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <header className={`topbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="shell topbar-inner">
        <Link to="/" className="brand">
          <div className="logo-container">
            <img src={Logo} alt="OneSoft Logo" className="logo-image" />
          </div>
        </Link>

        {/* Global Nav */}
        <nav className={`nav ${menuOpen ? 'mobile-is-open' : ''}`}>
          <div className="nav-list">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="nav-item-container"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`nav-link ${location.pathname.startsWith(item.href) ? 'active' : ''}`}
                  onClick={(e) => {
                    if (item.dropdown && window.innerWidth <= 768) {
                      e.preventDefault();
                      setActiveDropdown(activeDropdown === item.label ? null : item.label);
                    }
                  }}
                >
                  <span className="nav-icon text-gradient">{item.icon}</span>
                  <span className="nav-label-text">{item.label}</span>
                  {item.dropdown && <ChevronDown size={14} className={`caret ${activeDropdown === item.label ? 'rotated' : ''}`} />}
                </Link>

                {item.dropdown && (activeDropdown === item.label || (menuOpen && activeDropdown === item.label)) && (
                  <div className="dropdown-menu">
                    {item.dropdown.map((sub) => (
                      <Link key={sub.label} to={sub.href} className="dropdown-item">
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mobile-only mobile-actions" style={{ padding: '20px' }}>
            <Link to="/contact" className="primary-btn" style={{ width: '100%', textAlign: 'center', display: 'block' }}>Book Call</Link>
          </div>
        </nav>

        <div className="top-actions desktop-only">
          <Link to="/contact" className="primary-btn small">Book Call</Link>
        </div>

        <button
          className="menu-toggle mobile-only"
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${menuOpen ? 'active' : ''}`}></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
